import React, { useState, useEffect } from 'react';
import {
  FileText,
  Upload,
  Save,
  ArrowLeft,
  Image as ImageIcon,
  User,
  BookOpen,
  Tag,
  FileType,
  Globe,
  Eye,
  AlertCircle,
  Plus,
  Trash2,
  Cloud,
  CheckCircle
} from 'lucide-react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import cloudinaryService from '../services/cloudinary';

// Get base URL from environment or use default
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://vs-back.thundergits.com';

// Helper function to get full URL for book files
const getFileUrl = (filePath) => {
  if (!filePath) return '';
  // If it's already a full URL (starts with http), return as is
  if (filePath.startsWith('http')) return filePath;
  // Otherwise, construct the full URL
  // Remove /api from base URL if present and add the file path
  const baseUrl = API_BASE_URL.replace('in/api', 'in');
  return `${baseUrl}/${filePath}`;
};

function AddELibrary() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Check if we're in edit mode
  const isEditMode = searchParams.get('edit') !== null;
  const editBookId = searchParams.get('edit');
  const bookData = location.state?.bookData;

  const [formData, setFormData] = useState({
    name: '',
    author: '',
    description: '',
    category: '',
    language: 'English',
    status: 'draft',
    iconUrl: '',
    pdfUrl: '',
    hasChapter: false,
    chapters: []
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [errors, setErrors] = useState({});

  // Track uploaded files
  const [uploadedFiles, setUploadedFiles] = useState({
    icon: null,
    mainFile: null
  });

  // Track actual file objects for direct upload
  const [fileObjects, setFileObjects] = useState({
    iconFile: null,
    pdfFile: null
  });

  // Pre-fill form data when in edit mode
  useEffect(() => {
    if (isEditMode && bookData) {
      setFormData({
        name: bookData.name || bookData.title || '',
        author: bookData.author || '',
        description: bookData.description || '',
        category: bookData.category || '',
        language: bookData.language || 'English',
        status: bookData.status || 'draft',
        iconUrl: bookData.icon || bookData.image || '',
        pdfUrl: bookData.pdfUrl || '',
        hasChapter: bookData.hasChapter || false,
        chapters: bookData.chapters || []
      });

      // Set image preview if icon exists
      if (bookData.icon || bookData.image) {
        setImagePreview(getFileUrl(bookData.icon || bookData.image));
      }
    }
  }, [isEditMode, bookData]);

  const categories = [
    'Sacred Texts',
    'Mathematics',
    'Ayurveda',
    'Epic Literature',
    'Spirituality',
    'Yoga',
    'Philosophy',
    'Meditation',
    'Astrology',
    'Vedic Science'
  ];

  const languages = [
    'English',
    'Hindi',
    'Sanskrit',
    'Sanskrit/English',
    'Hindi/English',
    'Tamil',
    'Telugu',
    'Bengali',
    'Gujarati',
    'Marathi'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file
      const validation = cloudinaryService.validateFile(file, {
        maxSize: 5 * 1024 * 1024, // 5MB
        allowedTypes: ['.jpg', '.jpeg', '.png', '.gif', '.webp']
      });

      if (!validation.isValid) {
        toast.error(validation.errors[0]);
        return;
      }

      // Create preview immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Store the file object for later upload
      setFileObjects(prev => ({
        ...prev,
        iconFile: file
      }));

      // Mark as ready to upload
      setFormData(prev => ({
        ...prev,
        iconUrl: 'pending' // Temporary marker
      }));

      toast.success(`Icon \"${file.name}\" selected and ready to upload!`);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file
      const validation = cloudinaryService.validateFile(file, {
        maxSize: 200 * 1024 * 1024, // 50MB
        allowedTypes: ['.pdf']
      });

      if (!validation.isValid) {
        toast.error(validation.errors[0]);
        return;
      }

      // Store the file object for later upload
      setFileObjects(prev => ({
        ...prev,
        pdfFile: file
      }));

      // Mark as ready to upload
      setFormData(prev => ({
        ...prev,
        pdfUrl: 'pending' // Temporary marker
      }));

      toast.success(`Book file "${file.name}" selected and ready to upload!`);
    }
  };

  const handleToggleChapters = () => {
    setFormData(prev => ({
      ...prev,
      hasChapter: !prev.hasChapter,
      chapters: !prev.hasChapter ? [{ name: '', description: '', startPage: 1, endPage: 1 }] : []
    }));
  };

  const handleChapterChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      chapters: prev.chapters.map((chapter, i) =>
        i === index ? { ...chapter, [field]: value } : chapter
      )
    }));
  };


  const addChapter = () => {
    setFormData(prev => ({
      ...prev,
      chapters: [...prev.chapters, { name: '', description: '', startPage: 1, endPage: 1 }]
    }));
  };

  const removeChapter = (index) => {
    if (formData.chapters.length > 1) {
      setFormData(prev => ({
        ...prev,
        chapters: prev.chapters.filter((_, i) => i !== index)
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.author.trim()) {
      newErrors.author = 'Author is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (formData.hasChapter) {
      if (formData.chapters.length === 0) {
        newErrors.chapters = 'At least one chapter is required';
      } else {
        formData.chapters.forEach((chapter, index) => {
          if (!chapter.name.trim()) {
            newErrors[`chapter_name_${index}`] = `Chapter ${index + 1} name is required`;
          }
          if (!chapter.description.trim()) {
            newErrors[`chapter_description_${index}`] = `Chapter ${index + 1} description is required`;
          }
          if (!chapter.startPage || chapter.startPage < 1) {
            newErrors[`chapter_startPage_${index}`] = `Chapter ${index + 1} start page is required and must be greater than 0`;
          }
          if (!chapter.endPage || chapter.endPage < 1) {
            newErrors[`chapter_endPage_${index}`] = `Chapter ${index + 1} end page is required and must be greater than 0`;
          }
          if (chapter.startPage && chapter.endPage && chapter.startPage > chapter.endPage) {
            newErrors[`chapter_pageRange_${index}`] = `Chapter ${index + 1} start page must be less than or equal to end page`;
          }
        });
      }
      // When using chapters, we still need the main PDF file
      if (!formData.pdfUrl) {
        newErrors.file = 'Book PDF file is required when using chapters';
      }
    } else {
      if (!formData.pdfUrl) {
        newErrors.file = 'Book file is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setUploadProgress(prev => ({ ...prev, mainFile: 0 }));

    try {
      // Prepare FormData for file upload
      const formDataToSend = new FormData();

      // Add the PDF file
      if (fileObjects.pdfFile) {
        formDataToSend.append('book', fileObjects.pdfFile);
      }

      // Add the icon file if available
      if (fileObjects.iconFile) {
        formDataToSend.append('icon', fileObjects.iconFile);
      }

      // Add other form fields
      formDataToSend.append('name', formData.name);
      formDataToSend.append('author', formData.author);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('language', formData.language);
      formDataToSend.append('status', formData.status);
      formDataToSend.append('hasChapter', formData.hasChapter);

      // Add chapters if present
      if (formData.hasChapter && formData.chapters.length > 0) {
        formDataToSend.append('chapters', JSON.stringify(
          formData.chapters.map(chapter => ({
            name: chapter.name,
            description: chapter.description,
            startPage: parseInt(chapter.startPage) || 1,
            endPage: parseInt(chapter.endPage) || 1
          }))
        ));
      }

      // Call the appropriate API based on mode
      let response;
      if (isEditMode) {
        // Update existing book
        response = await apiService.updateELibraryBook(editBookId, formDataToSend);
        if (response.success) {
          toast.success('Book updated successfully!');
          navigate('/elib');
        } else {
          throw new Error(response.message || 'Failed to update book');
        }
      } else {
        // Create new book - upload to server
        toast.loading('Uploading book to server...', { id: 'book-upload' });
        response = await apiService.createELibraryBook(formDataToSend);

        if (response.success) {
          toast.success('Book uploaded and added successfully!', { id: 'book-upload' });
          navigate('/elib');
        } else {
          throw new Error(response.message || 'Failed to create book');
        }
      }
    } catch (error) {
      console.error('Error creating book:', error);
      toast.error(error.message || 'Failed to add book. Please try again.', { id: 'book-upload' });
    } finally {
      setLoading(false);
      setUploadProgress(prev => ({ ...prev, mainFile: undefined }));
    }
  };

  const handleSaveAsDraft = async (e) => {
    e.preventDefault();
    setFormData(prev => ({ ...prev, status: 'draft' }));
    // Wait for state update then submit
    setTimeout(() => handleSubmit(e), 0);
  };

  const handlePublish = async (e) => {
    e.preventDefault();
    setFormData(prev => ({ ...prev, status: 'published' }));
    // Wait for state update then submit
    setTimeout(() => handleSubmit(e), 0);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/elib')}
            className="p-2 text-gray-400 hover:text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              {isEditMode ? 'Edit Book' : 'Add New Book'}
            </h1>
            <p className="mt-2 text-gray-600">
              {isEditMode ? 'Edit book details and update the digital library' : 'Add a new book to the digital library'}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <BookOpen className="w-5 h-5 mr-2 text-orange-600" />
                Basic Information
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${errors.name ? 'border-red-300' : 'border-gray-300'
                      }`}
                    placeholder="Enter book name"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.name}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Author *
                  </label>
                  <input
                    type="text"
                    name="author"
                    value={formData.author}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${errors.author ? 'border-red-300' : 'border-gray-300'
                      }`}
                    placeholder="Enter author name"
                  />
                  {errors.author && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.author}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${errors.description ? 'border-red-300' : 'border-gray-300'
                      }`}
                    placeholder="Enter book description"
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.description}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category *
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${errors.category ? 'border-red-300' : 'border-gray-300'
                        }`}
                    >
                      <option value="">Select category</option>
                      {categories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                    {errors.category && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.category}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Language
                    </label>
                    <select
                      name="language"
                      value={formData.language}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      {languages.map((language) => (
                        <option key={language} value={language}>
                          {language}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* File Upload */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Upload className="w-5 h-5 mr-2 text-orange-600" />
                File Upload
              </h2>

              <div className="space-y-6">
                {/* Has Chapters Toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Has Chapters
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Toggle if this book should be divided into chapters with page ranges
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleToggleChapters}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${formData.hasChapter ? 'bg-orange-600' : 'bg-gray-200'
                      }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.hasChapter ? 'translate-x-6' : 'translate-x-1'
                        }`}
                    />
                  </button>
                </div>

                {/* Conditional File Upload */}
                {formData.hasChapter ? (
                  /* Chapter-by-Chapter Upload */
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-md font-medium text-gray-900">Chapters</h3>
                      <button
                        type="button"
                        onClick={addChapter}
                        className="inline-flex items-center px-3 py-1 border border-orange-300 text-sm font-medium rounded-md text-orange-700 bg-orange-50 hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Chapter
                      </button>
                    </div>

                    {formData.chapters.map((chapter, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-medium text-gray-700">
                            Chapter {index + 1}
                          </h4>
                          {formData.chapters.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeChapter(index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Chapter Name *
                          </label>
                          <input
                            type="text"
                            value={chapter.name}
                            onChange={(e) => handleChapterChange(index, 'name', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${errors[`chapter_name_${index}`] ? 'border-red-300' : 'border-gray-300'
                              }`}
                            placeholder={`Enter chapter ${index + 1} name`}
                          />
                          {errors[`chapter_name_${index}`] && (
                            <p className="mt-1 text-sm text-red-600 flex items-center">
                              <AlertCircle className="w-4 h-4 mr-1" />
                              {errors[`chapter_name_${index}`]}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Chapter Description *
                          </label>
                          <textarea
                            value={chapter.description}
                            onChange={(e) => handleChapterChange(index, 'description', e.target.value)}
                            rows={3}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${errors[`chapter_description_${index}`] ? 'border-red-300' : 'border-gray-300'
                              }`}
                            placeholder={`Enter chapter ${index + 1} description`}
                          />
                          {errors[`chapter_description_${index}`] && (
                            <p className="mt-1 text-sm text-red-600 flex items-center">
                              <AlertCircle className="w-4 h-4 mr-1" />
                              {errors[`chapter_description_${index}`]}
                            </p>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Start Page *
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={chapter.startPage || ''}
                              onChange={(e) => handleChapterChange(index, 'startPage', parseInt(e.target.value) || 1)}
                              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${errors[`chapter_startPage_${index}`] ? 'border-red-300' : 'border-gray-300'
                                }`}
                              placeholder="1"
                            />
                            {errors[`chapter_startPage_${index}`] && (
                              <p className="mt-1 text-sm text-red-600 flex items-center">
                                <AlertCircle className="w-4 h-4 mr-1" />
                                {errors[`chapter_startPage_${index}`]}
                              </p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              End Page *
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={chapter.endPage || ''}
                              onChange={(e) => handleChapterChange(index, 'endPage', parseInt(e.target.value) || 1)}
                              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${errors[`chapter_endPage_${index}`] ? 'border-red-300' : 'border-gray-300'
                                }`}
                              placeholder="1"
                            />
                            {errors[`chapter_endPage_${index}`] && (
                              <p className="mt-1 text-sm text-red-600 flex items-center">
                                <AlertCircle className="w-4 h-4 mr-1" />
                                {errors[`chapter_endPage_${index}`]}
                              </p>
                            )}
                          </div>
                        </div>

                        {errors[`chapter_pageRange_${index}`] && (
                          <p className="mt-1 text-sm text-red-600 flex items-center">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {errors[`chapter_pageRange_${index}`]}
                          </p>
                        )}
                      </div>
                    ))}

                    {errors.chapters && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.chapters}
                      </p>
                    )}
                  </div>
                ) : null}

                {/* Main Book File Upload - Always Required */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {formData.hasChapter ? 'Main Book PDF File *' : 'Book File * (PDF, EPUB, MOBI, TXT, DOC, DOCX)'}
                  </label>
                  <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${uploadProgress.mainFile !== undefined ? 'border-orange-400 bg-orange-50' :
                    formData.pdfUrl ? 'border-green-400 bg-green-50' :
                      'border-gray-300 hover:border-orange-400'
                    }`}>
                    <input
                      type="file"
                      onChange={handleFileChange}
                      accept=".pdf,.epub,.mobi,.txt,.doc,.docx"
                      className="hidden"
                      id="book-file"
                      disabled={uploadProgress.mainFile !== undefined}
                    />
                    <label htmlFor="book-file" className={`cursor-pointer ${uploadProgress.mainFile !== undefined ? 'cursor-not-allowed' : ''
                      }`}>
                      <div className="flex flex-col items-center">
                        {uploadProgress.mainFile !== undefined ? (
                          <Cloud className="mx-auto h-12 w-12 text-orange-500 animate-pulse" />
                        ) : formData.pdfUrl ? (
                          <div className="relative">
                            <FileType className="mx-auto h-12 w-12 text-green-500" />
                            <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full p-1">
                              <CheckCircle className="w-4 h-4" />
                            </div>
                          </div>
                        ) : (
                          <FileType className="mx-auto h-12 w-12 text-gray-400" />
                        )}
                      </div>
                      <div className="mt-4">
                        {uploadProgress.mainFile !== undefined ? (
                          <p className="text-sm text-orange-600 font-medium">
                            Uploading to server...
                          </p>
                        ) : formData.pdfUrl && fileObjects.pdfFile ? (
                          <p className="text-sm text-green-600 font-medium">
                            ✓ {fileObjects.pdfFile.name} ready to upload
                          </p>
                        ) : formData.pdfUrl ? (
                          <p className="text-sm text-green-600 font-medium">
                            ✓ File already uploaded
                          </p>
                        ) : (
                          <p className="text-sm text-gray-600">
                            Click to upload book file (PDF only)
                          </p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          Maximum file size: 150MB
                        </p>
                      </div>
                    </label>
                  </div>
                  {errors.file && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.file}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Cover Image */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <ImageIcon className="w-5 h-5 mr-2 text-orange-600" />
                Cover Image
              </h2>

              <div className="space-y-4">
                <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${uploadProgress.icon !== undefined ? 'border-orange-400 bg-orange-50' :
                  formData.iconUrl ? 'border-green-400 bg-green-50' :
                    'border-gray-300 hover:border-orange-400'
                  }`}>
                  <input
                    type="file"
                    onChange={handleImageChange}
                    accept="image/*"
                    className="hidden"
                    id="cover-image"
                    disabled={uploadProgress.icon !== undefined}
                  />
                  <label htmlFor="cover-image" className={`cursor-pointer ${uploadProgress.icon !== undefined ? 'cursor-not-allowed' : ''
                    }`}>
                    {imagePreview ? (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Cover preview"
                          className="mx-auto h-32 w-24 object-cover rounded-lg"
                        />
                        {formData.iconUrl && (
                          <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full p-1">
                            <CheckCircle className="w-4 h-4" />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        {uploadProgress.icon !== undefined ? (
                          <Cloud className="mx-auto h-12 w-12 text-orange-500 animate-pulse" />
                        ) : (
                          <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                        )}
                      </div>
                    )}
                    <div className="mt-4">
                      {uploadProgress.icon !== undefined ? (
                        <p className="text-sm text-orange-600 font-medium">
                          Uploading to server...
                        </p>
                      ) : formData.iconUrl && fileObjects.iconFile ? (
                        <p className="text-sm text-green-600 font-medium">
                          ✓ {fileObjects.iconFile.name} ready to upload
                        </p>
                      ) : formData.iconUrl ? (
                        <p className="text-sm text-green-600 font-medium">
                          ✓ Uploaded to server
                        </p>
                      ) : (
                        <p className="text-sm text-gray-600">
                          {imagePreview ? 'Change cover image' : 'Click to upload cover image'}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        PNG, JPG up to 5MB
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Publishing Options */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Eye className="w-5 h-5 mr-2 text-orange-600" />
                Publishing
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>

                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={handleSaveAsDraft}
                    disabled={loading}
                    className="w-full px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : isEditMode ? 'Update as Draft' : 'Save as Draft'}
                  </button>

                  <button
                    type="button"
                    onClick={handlePublish}
                    disabled={loading}
                    className="w-full px-4 py-2 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors duration-200 disabled:opacity-50"
                  >
                    {loading ? (isEditMode ? 'Updating...' : 'Publishing...') : isEditMode ? 'Update Book' : 'Publish Book'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default AddELibrary;
